import { Service, inject, ConfigService } from '@piwikpro/platform';
import { createAction } from 'redux-actions';
import type { Store } from 'redux';
import { HttpClient, IRequest, NextFunction } from '@piwikpro/http-crate';
import {
  NotifyOnFail,
  NotifyAboutUpdate,
  NotifyAboutAddition,
  CustomNotification,
} from '@piwikpro/notification-crate';
import { Confirmation } from '@piwikpro/confirmation-crate';
import { NotFoundError } from '@piwikpro/http-crate/httpErrors';

export interface CookieInformationAccount {
  accountName: string
}

export interface CreateCookieInformationAccount {
  email: string
  domains: string[]
  userLanguage: 'en'
  consentLanguage: string[]
  acceptedEmailsAndOffers: boolean
}

export interface ConnectCookieInformationAccount {
  accountName: string
}

export interface ErrorResponse {
  detail: string
}

export interface ValidationError {
  [key: string]: string[] | Record<string, string[]>
}

interface ApiCookieInformationAccount {
  // eslint-disable-next-line camelcase
  account_name: string
}

export const cookieInformationAccountInfoFetchInitialized = createAction(
  '@CookieInformation/account/get/init',
);
export const cookieInformationAccountInfoFetchSucceeded = createAction<CookieInformationAccount | null, CookieInformationAccount | null>(
  '@CookieInformation/account/get/success',
  payload => payload,
);
export const cookieInformationAccountInfoFetchFailed = createAction(
  '@CookieInformation/account/get/failed',
);

export const cookieInformationAccountCreateSucceeded = createAction<CreateCookieInformationAccount, CreateCookieInformationAccount>(
  '@CookieInformation/account/create/success',
  payload => payload,
);

export const cookieInformationAccountConnectSucceeded = createAction<ConnectCookieInformationAccount, ConnectCookieInformationAccount>(
  '@CookieInformation/account/connect/success',
  payload => payload,
);

export const cookieInformationAccountCreateFormShown = createAction(
  '@CookieInformation/account/createForm/shown',
);

export const cookieInformationAccountConnectFormShown = createAction(
  '@CookieInformation/account/connectForm/shown',
);

@Service()
export class CookieInformationService {
  constructor(
    @inject('store') private readonly store: Store,
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFail: NotifyOnFail,
    @inject('NotificationCrate.interceptors.NotifyAboutAddition') private readonly notifyAboutAddition: NotifyAboutAddition,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdate: NotifyAboutUpdate,
    @inject('NotificationCrate.interceptors.CustomNotification') private readonly customNotification: CustomNotification,
    @inject('ConfirmationCrate.confirmation') private readonly confirmation: Confirmation,
    @inject('config') private readonly config: ConfigService,
  ) {}

  async getAccount(): Promise<CookieInformationAccount | null> {
    this.store.dispatch(cookieInformationAccountInfoFetchInitialized());

    try {
      const response = await this.httpClient.request({
        endpoint: `${this.config.get('COOKIE_INFORMATION_URL')}/integration`,
        method: 'GET',
        retryCondition: async (res) => {
          if (!res.ok && (res.status !== 404)) {
            return true;
          }

          return false;
        },
      })
        .intercept(this.notifyOnFail)
        .json()
        .send<ApiCookieInformationAccount>();

      const account = {
        accountName: response.account_name,
      };

      this.store.dispatch(cookieInformationAccountInfoFetchSucceeded(account));

      return account;
    } catch (err: any) {
      if (err instanceof NotFoundError) {
        this.store.dispatch(cookieInformationAccountInfoFetchSucceeded(null));

        return null;
      }

      this.store.dispatch(cookieInformationAccountInfoFetchFailed());

      return null;
    }
  }

  async createAccount(data: CreateCookieInformationAccount) {
    await this.httpClient.request({
      endpoint: `${this.config.get('COOKIE_INFORMATION_URL')}/integration`,
      method: 'POST',
      body: {
        user_email: data.email,
        domains: data.domains,
        user_language: data.userLanguage,
        site_languages: data.consentLanguage,
        accepted_emails_and_offers: data.acceptedEmailsAndOffers,
      },
    })
      .intercept(this.notifyAboutAddition)
      .json()
      .send<CreateCookieInformationAccount>();

    this.store.dispatch(cookieInformationAccountCreateSucceeded(data));

    return data;
  }

  async connectAccount(data: ConnectCookieInformationAccount) {
    await this.httpClient.request({
      endpoint: `${this.config.get('COOKIE_INFORMATION_URL')}/integration/connect`,
      method: 'POST',
      body: {
        account_name: data.accountName,
      },
    })
      .intercept(this.notifyAboutUpdate)
      .json()
      .send<ConnectCookieInformationAccount>();

    this.store.dispatch(cookieInformationAccountConnectSucceeded(data));

    return data;
  }

  async getIntegrationToken() {
    const response = await this.httpClient.request({
      endpoint: '/api/oauth/v1/cookie-information/integration',
      method: 'GET',
      retryCondition: async (res) => {
        if (!res.ok && (res.status !== 404)) {
          return true;
        }

        return false;
      },
    })
      .json()
      // eslint-disable-next-line camelcase
      .send<{ created_at: string }>();

    return { createdAt: response.created_at };
  }

  async createIntegrationToken() {
    const response = await this.httpClient.request({
      endpoint: '/api/oauth/v1/cookie-information/integration',
      method: 'POST',
    })
      .intercept(this.customNotification.withConfig({
        pending: null,
        success: null,
        error: {
          text: 'administration:cookieInformation.integrationToken.notifications.generateFailed',
        },
      }))
      .json()
      .send<{ credentials: string }>();

    return { credentials: response.credentials };
  }

  async removeIntegrationToken() {
    await this.confirmation.confirm({
      messages: [
        'administration:cookieInformation.integrationToken.confirmation.message',
        'administration:cookieInformation.integrationToken.confirmation.question',
      ],
      title: 'administration:cookieInformation.integrationToken.confirmation.title',
      applyBtnText: 'administration:cookieInformation.integrationToken.confirmation.removeButton',
      asyncAction: async () => {
        await this.httpClient.request({
          endpoint: '/api/oauth/v1/cookie-information/integration',
          method: 'DELETE',
        })
          .intercept(this.customNotification.withConfig({
            pending: {
              text: 'administration:cookieInformation.integrationToken.notifications.removing',
            },
            success: {
              text: 'administration:cookieInformation.integrationToken.notifications.removed',
            },
            error: {
              text: 'administration:cookieInformation.integrationToken.notifications.removeFailed',
            },
          }))
          .intercept({
            // 404 means the token is already gone — same outcome as a successful delete
            intercept: async (req: Required<IRequest>, next: NextFunction) => {
              try {
                return await next(req);
              } catch (error) {
                if (error instanceof NotFoundError) {
                  return null;
                }

                throw error;
              }
            },
          })
          .json()
          .send();
      },
    });
  }
}
